import Head from "next/head";
import Script from "next/script";
import { useEffect } from "react";
import NextScript from "next/script";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlinedFlag, MdOutlineEventSeat } from "react-icons/md";
import { Link, List, Stack, ListItem, Box, Typography } from "@mui/material";

import { Divider } from "@/shared/base/components/Divider/Divider";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";

import { createState } from "@/global/store/zustand_store";
import { useRouterExtended } from "@/global/hooks/useAppSystem";
import { constants } from "../../constants";

import type { TeamConfig } from "../types";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select a minimum 1 game to continue.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      "Click Continue and connect your My Blue Jackets Account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Suite",
    description: "Select the suites you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://am.ticketmaster.com/bluejackets/"
        >
          My Blue Jackets Account
        </Link>
        . Reach out to Live Fan Support for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "What if I have questions before booking?",
    description: (
      <>
        For questions, please contact the Blue Jackets ticket office at{" "}
        <Link
          href="tel:6142463350"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (614) 246-3350
        </Link>
        , via email at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="mailto:ticketsales@bluejackets.com"
        >
          ticketsales@bluejackets.com
        </Link>{" "}
        or submit{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://www.nhl.com/bluejackets/tickets/premium-central?utm_medium=Web&utm_source=Attend&utm_campaign=premium-rentals&AdContent=faq-click-here"
        >
          here
        </Link>{" "}
        and a ticket sales representative will reach back to you within 24
        business hours.
      </>
    ),
  },
  {
    title: "What's included in my premium reservation?",
    description: (
      <Stack>
        <List sx={{ py: 0 }}>
          <b>CBJ Suites</b>
          <ListItem sx={{ paddingX: 1 }}>
            Each suite includes 16 and four standing room only tickets, private
            restroom, option to purchase food and beverage and a suite
            attendant.
          </ListItem>
        </List>
        <List sx={{ py: 0 }}>
          <b>Loge Box</b>
          <ListItem sx={{ paddingX: 1 }}>
            Each Loge Box ranges from four to six seats and is located on the
            Club Level. Loge Boxes also include an in-seat service option.
          </ListItem>
        </List>
        <List sx={{ py: 0 }}>
          <b>Terrace Table</b>
          <ListItem sx={{ paddingX: 1 }}>
            Each Terrace Table includes four seats and is located on the Club
            Level. Terrace Tables also include an in-seat service option.
          </ListItem>
        </List>
        <List sx={{ py: 0 }}>
          <b>Party Towers</b>
          <ListItem sx={{ paddingX: 1 }}>
            Each Party Tower includes either 24 or 48 seats based on your
            selection, private restroom and option to purchase food and beverage
            and a suite attendant.
          </ListItem>
        </List>
        <List sx={{ py: 0 }}>
          <b>Club A</b>
          <ListItem sx={{ paddingX: 1 }}>
            Club A includes 16 seats in a private area inside the Diamond Cellar
            Club. In addition, Club A has private entrance, restrooms,
            all-inclusive food, beer and select wine.
          </ListItem>
        </List>
      </Stack>
    ),
  },
  {
    title: "Are there other options available, beyond what I am seeing?",
    description: (
      <>
        Yes, currently a limited number of options are available for purchase
        online. Group options in the seating bowl are available for groups of 10
        to 400. For more information about other seating options, click{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://www.nhl.com/bluejackets/tickets/premium-central?utm_medium=Web&utm_source=Attend&utm_campaign=premium-rentals&AdContent=faq-click-here"
        >
          here
        </Link>
        , email{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="mailto:ticketsales@bluejackets.com"
        >
          ticketsales@bluejackets.com
        </Link>{" "}
        or call{" "}
        <Link
          href="tel:6142463350"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (614) 246-3350
        </Link>
        .
      </>
    ),
  },
  {
    title: "I have more/less than 20 guests. Are there premium options for me?",
    description: (
      <>
        Yes, we have other premium areas in Nationwide Arena that can be
        purchased in pairs up to 400. For more information about other seating
        options, click{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://www.nhl.com/bluejackets/tickets/premium-central?utm_medium=Web&utm_source=Attend&utm_campaign=premium-rentals&AdContent=faq-click-here"
        >
          here
        </Link>
        , email{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="mailto:ticketsales@bluejackets.com"
        >
          ticketsales@bluejackets.com
        </Link>{" "}
        or call{" "}
        <Link
          href="tel:6142463350"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (614) 246-3350
        </Link>
        .
      </>
    ),
  },
  {
    title:
      "What happens after I purchase a Blue Jackets premium product online?",
    description:
      "After purchasing a Blue Jackets premium product, you will receive a confirmation email about your purchase sent to the email you used when making the purchase, information on how to access your tickets and information about catering (if applicable). In addition, a Blue Jackets representative will follow up with you with 24 business hours to answer any questions you may have.",
  },
  {
    title: "How do I find my tickets?",
    description: (
      <>
        Event tickets are delivered to your{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://am.ticketmaster.com/bluejackets/?_gl=1*1pf9s03*_ga*mty3mjuxmzm4nc4xnzuzndy2mta2*_ga_xzhcneqsgp*cze3ntk3mjuymdkkbzexocrnmsr0mtc1otcynju5osrqntgkbdakada.*_ga_zq2se7qkt5*cze3ntk3mjuymtekbzk5jgcxjhqxnzu5nzi2ntk4jgo1osrsmcroma.."
        >
          Columbus Blue Jackets account manager
        </Link>{" "}
        within an hour of your purchase. You will receive an email with
        information on accessing your tickets. If you have any questions, please
        do not hesitate to contact our ticket office at{" "}
        <Link
          href="tel:6142463350"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (614) 246-3350
        </Link>{" "}
        or via email at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="mailto:ticketsales@bluejackets.com"
        >
          ticketsales@bluejackets.com
        </Link>
        .
      </>
    ),
  },
  {
    title: "Is there someone I can reach out to after making my purchase?",
    description: (
      <>
        Yes, one of our dedicated ticket sales representatives will reach out
        within 24 business hours of your purchase to contact you and answer any
        questions you may have. If you have questions prior to hearing from
        someone in our office, please do not hesitate to contact us at{" "}
        <Link
          href="tel:6142463350"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (614) 246-3350
        </Link>{" "}
        or via email at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="mailto:ticketsales@bluejackets.com"
        >
          ticketsales@bluejackets.com
        </Link>
        .
      </>
    ),
  },
];

export const ColumbusBlueJacketsAdditionalDetailsDisclaimerTC = () => (
  <>
    By entering my name, checking the box above and clicking Submit Request, I
    hereby (i) agree to the{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://am.ticketmaster.com/bluejackets/terms"
    >
      Terms of Use
    </Link>{" "}
    (ii) agree that my reservation will not be complete unless and until I
    receive a notice from Columbus Blue Jackets confirming whether or not my
    reservation has been accepted in accordance with our agreement
  </>
);

export const RegistrationTerms = () => (
  <>
    By creating an account you agree to receive email notifications regarding
    your purchase and to the{" "}
    <Link
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      target={"_blank"}
      href="https://www.attend.tech/terms-of-use"
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.attend.tech/privacy-policy"
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const CheckoutTerms = () => (
  <>
    By purchasing you agree to receive SMS alerts regarding ticket delivery and
    agree to the{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.attend.tech/terms-of-use"
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.attend.tech/privacy-policy"
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const logeSuiteNames = [
  "LOGE A",
  "LOGE B",
  "LOGE C",
  "LOGE D",
  "LOGE E",
  "LOGE F",
  "LOGE G",
  "LOGE H",
  "LOGE I",
  "LOGE J",
  "LOGE K",
  "LOGE L",
  "LOGE M",
  "LOGE N",
  "LOGE O",
  "LOGE P",
  "LOGE Q",
  "LOGE R",
  "LOGE S",
  "LOGE T",
  "LOGE U",
  "LOGE V",
  "LOGE W",
  "LOGE X",
  "LOGE Y",
  "LOGE Z",
];

export const partySuiteNames = [
  "PARTY TOWER B",
  "PARTY TOWER C",
  "PARTY TOWER D",
  "PARTY TOWER E",
  "PARTY TOWER F",
];

export const inventoryCardSuiteModal = createState({
  title: "",
  isOpen: false,
  content: <></>,
});

export const ColumbusBlueJacketsInventoryCard = () => {
  const modal = inventoryCardSuiteModal();

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => <Box p={1}>{modal.content}</Box>}
    />
  );
};

export const LogeBoxesContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Loge Boxes & Terrace Tables
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Indulge yourself and your group in the epitome of luxury with a
          single-game Loge Boxe or Terrace Table rental. Designed to accommodate
          4-6 people, these exclusive seating options provide a private and
          intimate setting for you to enjoy the thrilling action on the ice.
          Choose the ultimate in luxury and make your Blue Jackets game an
          unforgettable experience at Nationwide Arena.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • Private seating for 4-6 guests
          <br />
          • In-suite F&B service with ability to pre-order
          <br />
          • Personal television
          <br />• Private refrigerator for beverage storage
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const SuitesContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Executive Suites
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Elevate your game-day experience with our Suite Rental option,
          offering a range of exclusive amenities and services for you and your
          guests. With the capacity to accommodate 20-24 attendees, our suites
          provide a luxurious and private setting to enjoy the excitement of
          Blue Jackets hockey.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • Private restroom
          <br />
          • Coat closet
          <br />
          • 2 large televisions
          <br />
          • In-suite F&B service with ability to pre-order
          <br />
          • In-suite refrigerator
          <br />
          • Exclusive Wi-Fi specific to the NetJets suite level
          <br />• Access to our NetJets Suite Level Bar
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const TerracesContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Loge Boxes & Terrace Tables
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Indulge yourself and your group in the epitome of luxury with a
          single-game Loge Boxe or Terrace Table rental. Designed to accommodate
          4-6 people, these exclusive seating options provide a private and
          intimate setting for you to enjoy the thrilling action on the ice.
          Choose the ultimate in luxury and make your Blue Jackets game an
          unforgettable experience at Nationwide Arena.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • Private seating for 4-6 guests
          <br />
          • In-seat food and beverage service
          <br />• Personal television
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const PartyTowersContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Party Towers
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          With the capacity to accommodate 24-48 guests, our Party Towers
          provide the ultimate VIP treatment. Whether you're hosting a corporate
          event, celebrating achievements, or simply enjoying a thrilling game
          with friends, our Party Towers offer an unparalleled experience with
          state-of-the-art facilities and impeccable service.
          <br />
          Party Tower amenities include:
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • 24-48 tickets
          <br />
          • Private restroom
          <br />
          • In-suite F&B service with ability to pre-order
          <br />
          • Personal attendant
          <br />• Flat-screen TVs
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const router = useRouterExtended();
  const { teamSchema } = props.teamConfig;

  useEffect(() => {
    const handleRouteChange = (path: string) => {
      try {
        const url = `${window.location.origin}/${path}`;
        if (window?.gtag) {
          window?.gtag("event", "page_view", {
            page_location: url,
          });
        }
      } catch (err) {
        console.log(err);
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      {/* Google Analytics (GA4) + Google Ads */}
      <Head>
        <NextScript
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-XBHGVPC03F"
        />

        <script
          id={`${teamSchema}-analytics-inline`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag() {
                dataLayer.push(arguments);
              }
              gtag('js', new Date());
              gtag('config', 'G-XBHGVPC03F');
              gtag('config', 'AW-391431882');
              gtag('config', 'AW-417050549');
            `,
          }}
        />
      </Head>

      {/* Mimecast Pixel */}
      <Script
        id={`${teamSchema}-mimecast-pixel`}
        strategy="afterInteractive"
        src="https://protect-usb.mimecast.com/s/bcE0Cvm66ZTx7MszJkIb"
      />

      {/* Bing UET Tag */}
      <Script
        id={`${teamSchema}-bing-uet`}
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,t,r,u){
              var f,n,i;
              w[u]=w[u]||[],f=function(){
                var o={ti:"56355005", enableAutoSpaTracking: true};
                o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")
              },
              n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){
                var s=this.readyState;
                s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)
              },
              i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)
            })(window,document,"script","//bat.bing.com/bat.js","uetq");
          `,
        }}
      />

      {/* Rlets Capture Config */}
      <Script
        id={`${teamSchema}-rlets`}
        strategy="afterInteractive"
        src="//cdn.rlets.com/capture_configs/d77/77b/e0c/19741d3b8643a54e37fa1f8.js"
      />

      {/* TradableBits SDK */}
      <Script
        id={`${teamSchema}-tradablebits-sdk`}
        strategy="afterInteractive"
        src="https://tradablebits.com/pixels/7177080/sdk.js"
      />

      {/* Conversion Pixel Scripts (fire on purchase) */}
      <Script
        id={`${teamSchema}-conversion-pixels`}
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
              try {
                var cartRes = e.detail.cartRes;
                var checkoutRes = e.detail.checkoutRes;

                // Google Ads - Purchase Event
                gtag("event", "purchase", {
                  transaction_id: checkoutRes.orderNumber,
                  value: cartRes.allInPrice,
                  currency: "USD"
                });

                // Google Ads - Conversion AW-391431882
                gtag('event', 'conversion', {
                  'send_to': 'AW-391431882/j2asCPjF8v8BEMqN07oB',
                  'value': 1.0,
                  'currency': 'USD',
                  'transaction_id': checkoutRes.orderNumber
                });

                // Google Ads - Conversion AW-417050549
                gtag('event', 'conversion', {
                  'send_to': 'AW-417050549/qzO9CM6V5v4BELXf7sYB',
                  'value': 1.0,
                  'currency': 'USD',
                  'transaction_id': checkoutRes.orderNumber
                });

                // Bing UET - Purchase Event
                window.uetq = window.uetq || [];
                window.uetq.push("event", "PRODUCT_PURCHASE", {
                  "ecomm_prodid": checkoutRes.orderNumber,
                  "ecomm_pagetype": "PURCHASE",
                  "revenue_value": cartRes.allInPrice,
                  "currency": "USD"
                });

                // TradableBits - Purchase Event
                window.tbEvents = window.tbEvents || [];
                window.tbEvents.push({
                  eventName: "purchase",
                  eventAmount: cartRes.allInPrice,
                  orderKey: checkoutRes.orderNumber,
                  quantity: cartRes.totalSeats || 1,
                  currency: "USD",
                  purchaseEventName: "${teamSchema}_event_purchase"
                });

                console.log('${teamSchema} conversion pixels fired.');
              } catch (error) {
                console.log('Error firing conversion pixels for ${teamSchema}', error);
              }
            });
          `,
        }}
      />
    </>
  );
};
